import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import ReceiptDetail from './ReceiptDetailModal'
import GenericAddButton from '../components/GenericAddButton'
import ReceiptAddModal from './ReceiptAddModal'
import ReceiptListModal from './ReceiptListModal'
import GenericListFilter from './GenericListFilter'
import FilterToggleButton from '../components/FilterToggleButton'
import GenericSearchToggle from '../components/GenericSearchToggle'
import useGenericListFilter from '../hooks/useGenericListFilter'
import useGenericTextSearch from '../hooks/useGenericTextSearch'
import useSharedModalUiState from '../hooks/useSharedModalUiState'
import TableSortLabel from '@mui/material/TableSortLabel'
import ResetTableButton from '../components/ResetTableButton'
import RefreshButton from '../components/RefreshButton'
import DetailButton from '../components/DetailButtonModal'
import TripsDetailButton from '../components/TripDetailButton'
import ReceiptDeleteModal from './ReceiptDeleteModal'
import TripDetail from './TripDetailModal'
import deleteIcon from '../assets/images/icons/delete.png'
import detailIcon from '../assets/images/icons/detail.png'
import tripIcon from '../assets/images/icons/trip.png'
import manualSymbol from '../assets/images/icons/manual_symbol.png'

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core'

import {
  SortableContext,
  horizontalListSortingStrategy,
  useSortable
} from '@dnd-kit/sortable'

import { CSS } from '@dnd-kit/utilities'

const receiptDetailFields = [
  ['date', 'TRIPLOG.RECEIPTS.TABLE.DATE'],
  ['vehicleId', 'TRIPLOG.RECEIPTS.TABLE.VEHICLE'],
  ['spz', 'vehicle_license_plate'],
  ['fuel_type', 'TRIPLOG.RECEIPTS.TABLE.FUEL_TYPE'],
  ['charging_type', 'TRIPLOG.RECEIPTS.TABLE.CHARGING_TYPE'],
  ['fuel_name', 'TRIPLOG.RECEIPTS.TABLE.FUEL_NAME'],
  ['unit_price', 'TRIPLOG.RECEIPTS.TABLE.UNIT_PRICE'],
  ['units', 'TRIPLOG.RECEIPTS.TABLE.UNITS'],
  ['unit', 'TRIPLOG.RECEIPTS.TABLE.UNIT'],
  ['total_price', 'TRIPLOG.RECEIPTS.TABLE.TOTAL_PRICE'],
  ['currency', 'TRIPLOG.RECEIPTS.TABLE.CURRENCY'],
  ['vat_rate', 'TRIPLOG.RECEIPTS.TABLE.VAT_RATE'],
  ['receipt_number', 'TRIPLOG.RECEIPTS.TABLE.RECEIPT_NUMBER'],
  ['uid', 'TRIPLOG.RECEIPTS.TABLE.UID'],
  ['payment_type', 'TRIPLOG.RECEIPTS.TABLE.PAYMENT_TYPE'],
  ['transaction_id', 'TRIPLOG.RECEIPTS.TABLE.TRANSACTION_ID'],
  ['store_name', 'TRIPLOG.RECEIPTS.TABLE.STORE_NAME'],
  ['store_address', 'TRIPLOG.RECEIPTS.TABLE.STORE_ADDRESS'],
  ['ico', 'TRIPLOG.RECEIPTS.TABLE.ICO'],
  ['dic', 'TRIPLOG.RECEIPTS.TABLE.DIC'],
  ['note', 'TRIPLOG.RECEIPTS.TABLE.NOTES']
]

const formatBonusAmount = (receipt) => {
  const raw = receipt?.bonus_amount
  const numeric = Number(raw)
  if (raw == null || raw === '' || Number.isNaN(numeric)) return '-'
  const currency = receipt?.currency || 'EUR'
  return `${numeric.toFixed(2)} ${currency}`
}

const formatReceiptUnits = (value) => {
  const numeric = Number(value)
  if (value == null || value === '' || Number.isNaN(numeric)) return '-'
  return numeric.toFixed(2)
}

const DEFAULT_COLUMNS = [
  { key: 'date', labelKey: 'TRIPLOG.RECEIPTS.TABLE.DATE' },
  { key: 'vehicleId', labelKey: 'TRIPLOG.RECEIPTS.TABLE.VEHICLE' },
  { key: 'spz', labelKey: 'vehicle_license_plate' },
  { key: 'fuel_name', labelKey: 'TRIPLOG.RECEIPTS.TABLE.FUEL_NAME' },
  { key: 'unit_price', labelKey: 'TRIPLOG.RECEIPTS.TABLE.UNIT_PRICE' },
  { key: 'units', labelKey: 'TRIPLOG.RECEIPTS.TABLE.UNITS' },
  { key: 'unit', labelKey: 'TRIPLOG.RECEIPTS.TABLE.UNIT' },
  { key: 'total_price', labelKey: 'TRIPLOG.RECEIPTS.TABLE.TOTAL_PRICE' },
  { key: 'currency', labelKey: 'TRIPLOG.RECEIPTS.TABLE.CURRENCY' },
  { key: 'vat_rate', labelKey: 'TRIPLOG.RECEIPTS.TABLE.VAT_RATE' },
  { key: 'store_name', labelKey: 'TRIPLOG.RECEIPTS.TABLE.STORE_NAME' }
]

function SortableHeaderCell({
  col,
  orderBy,
  order,
  handleSort,
  colWidths,
  handleMouseDown,
  t
}) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: col.key })

  const customWidth = colWidths[col.key]

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    ...(customWidth
      ? {
          width: customWidth,
          minWidth: customWidth,
          maxWidth: customWidth
        }
      : {
          flex: '1 1 0',
          minWidth: 0
        }),
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    paddingRight: 8,
    boxSizing: 'border-box'
  }

  return (
    <div ref={setNodeRef} style={style}>
      <span
        {...attributes}
        {...listeners}
        style={{
          cursor: 'grab',
          marginRight: 6,
          color: '#999'
        }}
      >
        ⠿
      </span>

      <TableSortLabel
        active={orderBy === col.key}
        direction={orderBy === col.key ? order : 'asc'}
        onClick={() => handleSort(col.key)}
      >
        {t(col.labelKey)}
      </TableSortLabel>

      <div
        style={{
          position: 'absolute',
          right: 4,
          top: '20%',
          bottom: '20%',
          width: 1,
          background: '#9e9e9e'
        }}
      />

      <div
        onMouseDown={(e) => handleMouseDown(e, col.key)}
        style={{
          position: 'absolute',
          right: 0,
          top: 0,
          bottom: 0,
          width: 6,
          cursor: 'col-resize'
        }}
      />
    </div>
  )
}

const ReceiptRow = React.memo(function ReceiptRow({
  receipt,
  columns,
  colWidths,
  rowFontSize,
  isActive,
  isHovered,
  detailLabel,
  getReceiptVehicleLabel,
  getReceiptVehicleSpz,
  onOpenReceipt,
  onHoverReceipt,
  onClearHover,
  setShowFullscreenHint,
  setTooltipTimer,
  setCursorPos,
  tooltipTimer,
  onDeleteReceipt,
  detailColWidth,
  onOpenTrip,
  t
}) {

const isManual = receipt.manual === true
const isBonusPaid = receipt.bonus_paid === true
const hasTrip = Boolean(receipt?.tripId || receipt?.trip_id)
const bonusHint = `${t('TRIPLOG.RECEIPTS.BONUS')}: ${formatBonusAmount(receipt)}`

  return (
    <div
      onClick={() => onOpenReceipt(receipt, true)}
onMouseEnter={(e) => {
  if (e.target.closest('.vehicle-detail-button')) return

  if (tooltipTimer) clearTimeout(tooltipTimer)
  setShowFullscreenHint(false)

  onHoverReceipt(receipt)
}}
      onMouseMove={(e) => {
        if (e.target.closest('.vehicle-detail-button')) {
          onClearHover()
        } else {
          onHoverReceipt(receipt)
        }
      }}
      onMouseLeave={(e) => {
        const related = e.relatedTarget
        if (related && related.closest && related.closest('.vehicle-tooltip')) return
        onClearHover()
      }}
      style={{
        display: 'flex',
        padding: '4px 2px 4px 12px',
        borderTop: '1px solid #ececec',
        alignItems: 'center',
	borderLeft: isManual ? '3px solid #06857a' : '3px solid transparent',

	backgroundColor:
	  isActive ? '#e6f4f1' :
	  isHovered ? '#f3fafa' :
	  isManual ? '#f6ffff' : '#fff',
        cursor: 'pointer',
        fontSize: rowFontSize
      }}
    >
{columns.map(col => (
  <div
    key={col.key}
    style={{
      ...(colWidths[col.key]
        ? {
            width: colWidths[col.key],
            minWidth: colWidths[col.key],
            maxWidth: colWidths[col.key]
          }
        : {
            flex: '1 1 0',
            minWidth: 0
          }),
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }}
  >
    {col.key === 'vehicleId'
      ? getReceiptVehicleLabel(receipt)
      : col.key === 'spz'
        ? getReceiptVehicleSpz(receipt)
: col.key === 'date'
  ? (
      <span style={{ display: 'block', position: 'relative' }}>
        <span>
{receipt.date ? (
  <>
    <div>{new Date(receipt.date).toLocaleDateString('sk-SK')}</div>
    <div style={{ fontSize: 12, color: '#666' }}>
      {new Date(receipt.date).toLocaleTimeString('sk-SK')}
    </div>
  </>
) : '-'}
        </span>

{(receipt.bonus_paid === true || receipt.manual === true) && (
  <span
    onMouseEnter={(e) => {
      const timer = setTimeout(() => {
        if (receipt.bonus_paid === true) {
          setShowFullscreenHint(bonusHint)
        } else if (receipt.manual === true) {
          setShowFullscreenHint(t('TRIPLOG.RECEIPTS.MANUAL_LABEL'))
        }
      }, 600)

      setTooltipTimer(timer)
    }}
    onMouseLeave={() => {
      if (tooltipTimer) clearTimeout(tooltipTimer)
      setShowFullscreenHint(false)
      setTooltipTimer(null)
    }}
    onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
    style={{
      position: 'absolute',
      right: 20,
      bottom: -2,
      display: 'flex',
      alignItems: 'center',
      gap: 4
    }}
  >
    {receipt.bonus_paid === true && (
      <img
        src={require('../assets/images/icons/bonus_symbol.png')}
        alt="bonus"
        style={{
          width: 16,
          height: 14
        }}
      />
    )}

    {receipt.manual === true && (
      <img
        src={manualSymbol}
        alt="manual"
        style={{
          width: 16,
          height: 14
        }}
      />
    )}
  </span>
)}
      </span>
    )
      : col.key === 'store_name' && receipt.store_name === 'manual-entry'
        ? '-'
  : col.key === 'payment_type' && receipt.payment_type === 'manual'
    ? '-'
    : col.key === 'units'
      ? formatReceiptUnits(receipt.units)
    : receipt[col.key] ?? '-'}
  </div>
))}
<div
  onClick={(e) => e.stopPropagation()}
  onMouseDown={(e) => e.stopPropagation()}
  onMouseEnter={() => {
    if (!hasTrip) return

    const timer = setTimeout(() => {
      setShowFullscreenHint(t('TRIPLOG.DETAIL_TITLE'))
    }, 600)

    setTooltipTimer(timer)
  }}
  onMouseLeave={() => {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => {
    if (!hasTrip) return
    setCursorPos({ x: e.clientX, y: e.clientY })
  }}
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  }}
>
  <TripsDetailButton
    active={hasTrip}
    onClick={(e) => {
      e.stopPropagation()
      if (!hasTrip) return
      onOpenTrip(receipt)
    }}
  />
</div>

<div
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 8
  }}
>
  <DetailButton
    onClick={(e) => {
      e.stopPropagation()
      onOpenReceipt(receipt, false)
    }}
  />
</div>

<div
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 8,
    marginLeft: 8
  }}
>
  <div
    onMouseEnter={(e) => {
      if (!isBonusPaid) return

      const timer = setTimeout(() => {
	setShowFullscreenHint(t('TRIPLOG.RECEIPTS.DELETE_ERROR'))
      }, 600)
      setTooltipTimer(timer)
    }}
    onMouseLeave={() => {
      if (tooltipTimer) clearTimeout(tooltipTimer)
      setShowFullscreenHint(false)
      setTooltipTimer(null)
    }}
    onMouseMove={(e) => {
      if (isBonusPaid) {
        setCursorPos({ x: e.clientX, y: e.clientY })
      }
    }}
    style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center'
    }}
  >
    <button
      disabled={isBonusPaid}
      style={{
        border: isBonusPaid ? '1.5px solid #999' : '1.5px solid #a33',
        background: '#f5f5f5',
        borderRadius: 6,
        height: 28,
        width: 35,
        cursor: isBonusPaid ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxShadow: '0 1px 8px rgba(0,0,0,0.30)',
        transition: 'transform 0.05s ease, box-shadow 0.1s ease',
        outline: 'none',
        opacity: isBonusPaid ? 0.6 : 1
      }}
      onMouseEnter={(e) => {
        if (isBonusPaid) return
        e.currentTarget.style.background = '#f3bcbc'
      }}
      onMouseLeave={(e) => {
        if (isBonusPaid) return
        e.currentTarget.style.background = '#f5f5f5'
        e.currentTarget.style.transform = 'scale(1)'
        e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
      }}
      onMouseDown={(e) => {
        if (isBonusPaid) return
        e.currentTarget.style.transform = 'scale(0.96)'
        e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
      }}
      onMouseUp={(e) => {
        if (isBonusPaid) return
        e.currentTarget.style.transform = 'scale(1)'
        e.currentTarget.style.boxShadow = '0 1px 8px rgba(0,0,0,0.30)'
        e.currentTarget.blur()
      }}
      onClick={(e) => {
        if (isBonusPaid) return
        e.stopPropagation()
        onDeleteReceipt(receipt)
      }}
    >
      <img
        src={deleteIcon}
        alt="delete"
        style={{
          width: 19,
          height: 19,
          pointerEvents: 'none'
        }}
      />
    </button>
  </div>
</div>
    </div>
  )

}, (prev, next) => {

  return (
    prev.receipt === next.receipt &&
    prev.columns === next.columns &&
    prev.colWidths === next.colWidths &&
    prev.rowFontSize === next.rowFontSize &&
    prev.isActive === next.isActive &&
    prev.isHovered === next.isHovered &&
    prev.detailLabel === next.detailLabel
  )

})

const ReceiptsBox = ({ user, t, layout, viewport }) => {
  const [receipts, setReceipts] = useState([])
  const [receiptsLoading, setReceiptsLoading] = useState(true)
  const [receiptsError, setReceiptsError] = useState(null)
  const [vehiclesById, setVehiclesById] = useState({})
  const [selectedReceipt, setSelectedReceipt] = useState(null)
const [selectedTrip, setSelectedTrip] = useState(null)
const [trips, setTrips] = useState([])
const [tripDetailOpen, setTripDetailOpen] = useState(false)
  const [receiptDetailOpen, setReceiptDetailOpen] = useState(false)
  const [receiptListFullscreen, setReceiptListFullscreen] = useState(false)
  const [hoveredReceipt, setHoveredReceipt] = useState(null)
  const [activeRow, setActiveRow] = useState(null)
const [tooltipTimer, setTooltipTimer] = useState(null)
  const [openExportDropdown, setOpenExportDropdown] = useState(false)
  const [addReceiptOpen, setAddReceiptOpen] = useState(false)
const [deleteReceipt, setDeleteReceipt] = useState(null)
const [deleteModalOpen, setDeleteModalOpen] = useState(false)

const { showSearch, setShowSearch, showFilter, setShowFilter } = useSharedModalUiState()
const [searchQuery, setSearchQuery] = useState('')

const [orderBy, setOrderBy] = useState(() => {
  const stored = localStorage.getItem('receiptOrderBy')
  return stored || 'date'
})

const [order, setOrder] = useState(() => {
  const stored = localStorage.getItem('receiptOrder')
  return stored || 'desc'
})

const [columns, setColumns] = useState(() => {
  const stored = localStorage.getItem('receiptColumns')
  if (stored) {
    const parsed = JSON.parse(stored)
    if (Array.isArray(parsed)) {
      return parsed.filter((col) => col?.key !== 'bonus_amount')
    }
  }

  return DEFAULT_COLUMNS
})

const [colWidths, setColWidths] = useState(() => {
  const stored = localStorage.getItem('receiptColWidths')
  return stored ? JSON.parse(stored) : {}
})

const sensors = useSensors(useSensor(PointerSensor))

const handleSort = (col) => {
  if (orderBy === col) {
    setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
  } else {
    setOrderBy(col)
    setOrder('asc')
  }
}

const handleMouseDown = (e, key) => {
  const startX = e.clientX
  const startWidth = colWidths[key] || 150

  const onMouseMove = (e) => {
    const delta = e.clientX - startX
    const newWidth = Math.max(80, startWidth + delta)
    setColWidths(prev => ({ ...prev, [key]: newWidth }))
  }

  const onMouseUp = () => {
    window.removeEventListener('mousemove', onMouseMove)
    window.removeEventListener('mouseup', onMouseUp)
  }

  window.addEventListener('mousemove', onMouseMove)
  window.addEventListener('mouseup', onMouseUp)
}

const handleDragEnd = (event) => {
  const { active, over } = event
  if (!over || active.id === over.id) return

  const oldIndex = columns.findIndex(c => c.key === active.id)
  const newIndex = columns.findIndex(c => c.key === over.id)

  const reordered = [...columns]
  const [moved] = reordered.splice(oldIndex, 1)
  reordered.splice(newIndex, 0, moved)

  setColumns(reordered)
}

  const [tooltipEnabled, setTooltipEnabled] = useState(() => {
    const stored = localStorage.getItem('tooltipEnabled')
    return stored === null ? true : stored === 'true'
  })

const [showFullscreenHint, setShowFullscreenHint] = useState(false)
const [cursorPos, setCursorPos] = useState({ x: 0, y: 0 })

  useEffect(() => {
    localStorage.setItem('tooltipEnabled', tooltipEnabled)
  }, [tooltipEnabled])

useEffect(() => {
  localStorage.setItem('receiptColumns', JSON.stringify(columns))
}, [columns])

useEffect(() => {
  localStorage.setItem('receiptColWidths', JSON.stringify(colWidths))
}, [colWidths])

useEffect(() => {
  localStorage.setItem('receiptOrderBy', orderBy)
}, [orderBy])

useEffect(() => {
  localStorage.setItem('receiptOrder', order)
}, [order])

  const loadReceipts = useCallback(async (opts = {}) => {
  const { silent = false } = opts
    if (!user?.id) return
    try {
      if (!silent) setReceiptsLoading(true)
      setReceiptsError(null)

      const res = await fetchWithAuth(`/users/${user.id}/receipts`)
      if (!res.ok) {
        setReceiptsError('Nepodarilo sa načítať účtenky.')
        return
      }

      const data = await res.json()
console.log('BACKEND RECEIPTS:', data)
      setReceipts(Array.isArray(data) ? data : [])
    } catch {
      setReceiptsError('Chyba pripojenia pri načítaní účteniek.')
    } finally {
      if (!silent) setReceiptsLoading(false)
    }
  }, [user?.id])

  const loadVehicles = useCallback(async () => {
    if (!user?.id) return

    try {
      const res = await fetchWithAuth(`/users/${user.id}/vehicles`)
      if (!res.ok) return

      const data = await res.json()
      if (!Array.isArray(data)) return

      const index = data.reduce((acc, vehicle) => {
        if (vehicle?.id == null) return acc
        acc[String(vehicle.id)] = vehicle
        return acc
      }, {})

      setVehiclesById(index)
    } catch {}
  }, [user?.id])

  const loadTrips = useCallback(async () => {
    if (!user?.id) return

    try {
      const res = await fetchWithAuth('/triplog/trips')
      if (!res.ok) return

      const data = await res.json()
      setTrips(Array.isArray(data) ? data : [])
    } catch {}
  }, [user?.id])

  useEffect(() => {
    loadReceipts()
  }, [loadReceipts])

  useEffect(() => {
    loadVehicles()
  }, [loadVehicles])

  useEffect(() => {
    loadTrips()
  }, [loadTrips])

const refreshTripsAndReceipts = useCallback((opts = {}) => {
  const { silent = false } = opts
  loadReceipts({ silent })
  loadTrips()
}, [loadReceipts, loadTrips])

const handleReceiptSaved = useCallback((opts = {}) => {
  const delayMs = Number(opts?.delayMs || 0)

  if (delayMs > 0) {
    setTimeout(() => refreshTripsAndReceipts({ silent: true }), delayMs)
    return
  }

  refreshTripsAndReceipts({ silent: true })
}, [refreshTripsAndReceipts])

const handleDeleteReceiptClick = useCallback((receipt) => {
  setDeleteReceipt(receipt)
  setDeleteModalOpen(true)
}, [])

const handleOpenTripFromReceipt = useCallback((receipt) => {
  const tripId = receipt?.tripId ?? receipt?.trip_id
  if (!tripId) return

  const mappedTrip = trips.find((trip) => String(trip?.id) === String(tripId))
  setSelectedTrip(mappedTrip || { id: tripId, userId: user?.id })
  setTripDetailOpen(true)
}, [trips, user?.id])

  const getReceiptVehicleLabel = useCallback((receipt) => {
    const vehicleFromList = vehiclesById[String(receipt?.vehicleId)]
    const vehicle = vehicleFromList || receipt?.vehicle
    return vehicle?.name || vehicle?.model || vehicle?.licensePlate || vehicle?.spz || '-'
  }, [vehiclesById])

const getReceiptVehicleSpz = useCallback((receipt) => {
  const vehicleFromList = vehiclesById[String(receipt?.vehicleId)]
  const vehicle = vehicleFromList || receipt?.vehicle
  return vehicle?.licensePlate || vehicle?.spz || '-'
}, [vehiclesById])

const getReceiptFilterData = useCallback((receipt) => ({
  vehicleId: receipt?.vehicleId,
  date: receipt?.date
}), [])

const {
  criteria: filterCriteria,
  setCriteria: setFilterCriteria,
  filteredItems: filteredReceipts
} = useGenericListFilter(receipts, getReceiptFilterData)

const isFiltered = Object.values(filterCriteria || {}).some(v => v && v !== '')

const vehicleOptions = useMemo(() => {
  return Object.values(vehiclesById).map((vehicle) => {
    const name = vehicle?.name || vehicle?.model || vehicle?.licensePlate || vehicle?.spz || `Vozidlo #${vehicle?.id}`
    const plate = vehicle?.licensePlate || vehicle?.spz || ''

    return {
      value: String(vehicle?.id),
      label: plate && name !== plate ? `${name} (${plate})` : name
    }
  })
}, [vehiclesById])

const getReceiptSearchData = useCallback((receipt) => {
  const vehicleFromList = vehiclesById[String(receipt?.vehicleId)]
  const vehicle = vehicleFromList || receipt?.vehicle

  return {
    ...receipt,
    vehicleName: vehicle?.name,
    vehicleModel: vehicle?.model,
    vehicleLicensePlate: vehicle?.licensePlate,
    vehicleSpz: vehicle?.spz,
    searchDate: receipt?.date ? new Date(receipt.date).toLocaleDateString() : ''
  }
}, [vehiclesById])

const searchFilteredReceipts = useGenericTextSearch(
  filteredReceipts,
  searchQuery,
  getReceiptSearchData
)

const filterLabels = useMemo(() => ({
  allVehicles: t('TRIPLOG.BUTTON.ALL_VEHICLES'),
  from: t('TRIPLOG.FILTER_FROM'),
  to: t('TRIPLOG.FILTER_TO'),
  month: t('TRIPLOG.FILTER_MONTH'),
  year: t('TRIPLOG.FILTER_YEAR'),
  clear: t('TRIPLOG.BUTTON.CLEAR')
}), [t])

const sortedReceipts = [...searchFilteredReceipts].sort((a, b) => {
  let aVal = a[orderBy]
  let bVal = b[orderBy]

  if (orderBy === 'vehicleId') {
    aVal = getReceiptVehicleLabel(a)
    bVal = getReceiptVehicleLabel(b)
  }

if (orderBy === 'spz') {
  aVal = getReceiptVehicleSpz(a)
  bVal = getReceiptVehicleSpz(b)
}

  if (orderBy === 'date') {
    aVal = aVal ? new Date(aVal).getTime() : 0
    bVal = bVal ? new Date(bVal).getTime() : 0
  }

  if (
    orderBy === 'unit_price' ||
    orderBy === 'units' ||
    orderBy === 'total_price' ||
    orderBy === 'vat_rate'
  ) 
{
    aVal = Number(aVal) || 0
    bVal = Number(bVal) || 0
  }

  if (typeof aVal === 'string') aVal = aVal.toLowerCase()
  if (typeof bVal === 'string') bVal = bVal.toLowerCase()

  if (aVal > bVal) return order === 'asc' ? 1 : -1
  if (aVal < bVal) return order === 'asc' ? -1 : 1
  return 0
})

const selectedReceiptIndex = sortedReceipts.findIndex(
  (item) => item?.id === selectedReceipt?.id
)

const handlePrevReceipt = () => {
  if (selectedReceiptIndex <= 0) return
  const prevReceipt = sortedReceipts[selectedReceiptIndex - 1]
  setSelectedReceipt(prevReceipt)
  setActiveRow(prevReceipt?.id ?? null)
}

const handleNextReceipt = () => {
  if (selectedReceiptIndex < 0 || selectedReceiptIndex >= sortedReceipts.length - 1) return
  const nextReceipt = sortedReceipts[selectedReceiptIndex + 1]
  setSelectedReceipt(nextReceipt)
  setActiveRow(nextReceipt?.id ?? null)
}

  const detailColWidth = viewport <= 900 ? 110 : 50

  return (
    <div style={{
      background: '#ededed',
      padding: 12,
      border: '1.5px solid #1e3f44',
      borderRadius: 10,
      width: '100%',
      boxSizing: 'border-box',
        position: 'relative',
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column'
    }}>
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        display: 'flex',
        alignItems: 'center',
        gap: 8
      }}>

  <GenericAddButton
    onToggle={() => setAddReceiptOpen(true)}
  />

<GenericSearchToggle
  visible={showSearch}
  query={searchQuery}
  onQueryChange={setSearchQuery}
  onToggle={() => {
    setShowSearch(prev => {
      if (prev) setSearchQuery('')
      return !prev
    })
  }}
  placeholder={t('TRIPLOG.SEARCH_PLACEHOLDER', { defaultValue: 'Hľadať...' })}
/>

<FilterToggleButton
  visible={showFilter}
  active={isFiltered}
  onToggle={() => setShowFilter(prev => !prev)}
/>
        <label style={{
          display: 'flex',
          alignItems: 'center',
          gap: 4,
          cursor: 'pointer',
          fontSize: 13,
          userSelect: 'none',
    	  marginTop: 5
        }}>
          <input
            type="checkbox"
            checked={tooltipEnabled}
            onChange={(e) => setTooltipEnabled(e.target.checked)}
            style={{
              cursor: 'pointer',
              appearance: 'none',
              width: 16,
              height: 16,
              border: '1px solid #1e3f44',
              borderRadius: 3,
              background: tooltipEnabled ? '#2da9a4' : '#fff'
            }}
          />
          {t('TRIPLOG.SERVICECOSTS.TOGGLE')}
        </label>

<RefreshButton onClick={() => loadReceipts({ silent: true })} />

<ResetTableButton
  isModified={
    Object.keys(colWidths).length > 0 ||
    JSON.stringify(columns.map(c => c.key)) !== JSON.stringify(DEFAULT_COLUMNS.map(c => c.key)) ||
    orderBy !== 'date' ||
    order !== 'desc'
  }
  onReset={() => {

const resetCols = DEFAULT_COLUMNS

    setColumns(resetCols)
    setColWidths({})
    setOrderBy('date')
    setOrder('desc')

    localStorage.removeItem('receiptColumns')
    localStorage.removeItem('receiptColWidths')
  }}
/>

<button
  onMouseEnter={(e)=>{
    e.currentTarget.style.background='#2da9a4'
  }}
  onMouseLeave={(e)=>{
    e.currentTarget.style.background='#f5f5f5'
    e.currentTarget.style.transform='scale(1)'
    e.currentTarget.style.boxShadow='0 1px 8px rgba(0,0,0,0.30)'
  }}
  onMouseDown={(e)=>{
    e.currentTarget.style.transform='scale(0.96)'
    e.currentTarget.style.boxShadow='0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e)=>{
    e.currentTarget.style.transform='scale(1)'
    e.currentTarget.style.boxShadow='0 1px 8px rgba(0,0,0,0.30)'
  }}
  onClick={(e)=>{
    e.currentTarget.blur()
    setOpenExportDropdown(true)
    setReceiptListFullscreen(true)
  }}
  style={{
    background:'#f5f5f5',
    border:'1px solid #1e3f44',
    borderRadius:6,
    height:24,
    padding:'0 10px',
    cursor:'pointer',
    fontWeight:700,
    boxShadow:'0 1px 8px rgba(0,0,0,0.30)',
    transition:'transform 0.05s ease, box-shadow 0.1s ease',
    marginTop:5
  }}
>
  {t('TRIPLOG.BUTTON_EXPORT')}
</button>

<button
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(true)
    }, 600)

    setTooltipTimer(timer)
  }}
  onMouseLeave={(e) => {
    if (tooltipTimer) clearTimeout(tooltipTimer)

    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  onMouseDown={(e) => {
    e.currentTarget.style.transform = 'scale(0.96)'
    e.currentTarget.style.boxShadow = '0 0px 4px rgba(0,0,0,0.25)'
  }}
  onMouseUp={(e) => {
    e.currentTarget.style.transform = 'scale(1)'
    e.currentTarget.style.boxShadow = 'none'
  }}
  onClick={(e) => {
    if (tooltipTimer) clearTimeout(tooltipTimer)

    e.currentTarget.blur()
    setReceiptListFullscreen(true)
  }}
  style={{
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    fontSize: 32
  }}
>
  ⛶
</button>
      </div>

      <h3 style={{ fontSize: 18, marginTop: 0, marginBottom: 10 }}>
        {t('TRIPLOG.TAB.RECEIPTS')}
      </h3>

      {receiptsLoading && !receiptDetailOpen && <div>Načítavam...</div>}
      {receiptsError && <div style={{ color: 'red' }}>{receiptsError}</div>}

{showFilter && (
  <GenericListFilter
    value={filterCriteria}
    onChange={setFilterCriteria}
    vehicles={vehicleOptions}
    labels={filterLabels}
  />
)}

<div style={{
  border: '1px solid #c7c7c7',
  borderRadius: 10,
  background: '#fff',
  flex: 1,
  minHeight: 0,
  maxWidth: '100%',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden'
}}>

<DndContext
  sensors={sensors}
  collisionDetection={closestCenter}
  onDragEnd={handleDragEnd}
>
  <SortableContext
    items={columns.map(c => c.key)}
    strategy={horizontalListSortingStrategy}
  >
    <div
      style={{
        display: 'flex',
        background: '#c8e1e1',
        padding: '8px 8px',
        fontWeight: 700,
        fontSize: layout.vehicleHeaderFontSize
      }}
    >
      {columns.map(col => (
        <SortableHeaderCell
          key={col.key}
          col={col}
          orderBy={orderBy}
          order={order}
          handleSort={handleSort}
          colWidths={colWidths}
          handleMouseDown={handleMouseDown}
	  t={t}
        />
      ))}

<div
  onMouseEnter={() => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(t('TRIPLOG.RECEIPTS.RELATED_TRIP'))
    }, 600)

    setTooltipTimer(timer)
  }}
  onMouseLeave={() => {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
>
  <img
    src={tripIcon}
    alt="trip"
    style={{ width: 22, height: 22 }}
  />
</div>

<div
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(t('TRIPLOG.RECEIPTS.DETAIL'))
    }, 600)
    setTooltipTimer(timer)
  }}
  onMouseLeave={() => {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
>
  <img
    src={detailIcon}
    alt="detail"
    style={{ width: 22, height: 22 }}
  />
</div>

<div
  onMouseEnter={(e) => {
    const timer = setTimeout(() => {
      setShowFullscreenHint(t('TRIPLOG.RECEIPTS.DELETE_RECORD'))
    }, 600)
    setTooltipTimer(timer)
  }}
  onMouseLeave={() => {
    if (tooltipTimer) clearTimeout(tooltipTimer)
    setShowFullscreenHint(false)
  }}
  onMouseMove={(e) => setCursorPos({ x: e.clientX, y: e.clientY })}
  style={{
    flex: `0 0 ${detailColWidth}px`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
>
  <img
    src={deleteIcon}
    alt="delete"
    style={{
      width: 22,
      height: 22
    }}
  />
</div>
    </div>
  </SortableContext>
</DndContext>
<div
  style={{
    overflow: 'auto',
    flex: 1,
    minHeight: 0
  }}
>
{sortedReceipts.map(r => (
<ReceiptRow
  key={r.id}
  receipt={r}
  columns={columns}
  colWidths={colWidths}
  rowFontSize={layout.vehicleRowFontSize}
  isActive={activeRow === r.id}
  isHovered={hoveredReceipt?.id === r.id}
  detailLabel={t('TRIPLOG.BUTTON.DETAIL')}
  getReceiptVehicleLabel={getReceiptVehicleLabel}
  getReceiptVehicleSpz={getReceiptVehicleSpz}
  setShowFullscreenHint={setShowFullscreenHint}
  setTooltipTimer={setTooltipTimer}
  setCursorPos={setCursorPos}
  tooltipTimer={tooltipTimer}
  t={t}
  onOpenReceipt={(receipt, markRow = false) => {
    if (markRow) setActiveRow(receipt.id)
    setSelectedReceipt(receipt)
    setReceiptDetailOpen(true)
  }}
    onHoverReceipt={(receipt) => {
      setHoveredReceipt(prev => {
        if (prev?.id === receipt?.id) return prev
        return receipt
      })
    }}
    onClearHover={() => {
      setHoveredReceipt(prev => (prev == null ? prev : null))
    }}
      onDeleteReceipt={handleDeleteReceiptClick} 
      onOpenTrip={handleOpenTripFromReceipt}
  />
))}
</div>
      </div>

      {tooltipEnabled && hoveredReceipt && (
        <div
          className="vehicle-tooltip"
          onMouseLeave={() => setHoveredReceipt(null)}
              style={{
                position: 'fixed',
                right:
                  viewport >= 1920 ? 9 :
                  viewport >= 1600 ? 9 :
                  viewport >= 1366 ? 10 : 20,
                bottom:
                  viewport >= 1920 ? 50 :
                  viewport >= 1600 ? 50 :
                  viewport >= 1366 ? 49 : 30,
                fontSize:
                  viewport >= 1920 ? 14 :
                  viewport >= 1600 ? 14 :
                  viewport >= 1366 ? 13 : 13,
                maxHeight:
                  viewport >= 1920 ? '80vh' :
                  viewport >= 1600 ? '75vh' : '96vh',
                background: '#f6ffff',
                border: '1px solid #1e3f44',
                borderRadius: 8,
                padding: '16px 20px',
                boxShadow: '0 8px 20px rgba(0,0,0,0.35)',
                zIndex: 9999,
                overflowY: 'auto'
              }}
            >
{receiptDetailFields.map(([key, labelKey]) => {
  if (
    (key === 'fuel_type' || key === 'charging_type') &&
    hoveredReceipt?.fuel_type !== 'electric'
  ) {
    return null
  }

  return (
    <div key={key} style={{ marginBottom: 4, display: 'flex' }}>
      <strong style={{ minWidth: 200 }}>
        {t(labelKey)}:
      </strong>

      <span style={{ color: '#1e3f44', fontWeight: 600 }}>
        {key === 'vehicleId'
          ? getReceiptVehicleLabel(hoveredReceipt)
          : key === 'spz'
            ? getReceiptVehicleSpz(hoveredReceipt)
            : key === 'date'
              ? (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span>
                      {hoveredReceipt.date
                        ? `${new Date(hoveredReceipt.date).toLocaleDateString('sk-SK')} | ${new Date(hoveredReceipt.date).toLocaleTimeString('sk-SK')}`
                        : '-'}
                    </span>

                    {hoveredReceipt?.manual === true && (
                      <span style={{ fontWeight: 'bold', color: '#d32f2f' }}>
                        ({t('TRIPLOG.RECEIPTS.MANUAL_LABEL')})
                      </span>
                    )}
                  </span>
                )
              : key === 'store_name' && hoveredReceipt.store_name === 'manual-entry'
                ? '-'
                : key === 'payment_type' && hoveredReceipt.payment_type === 'manual'
                  ? '-'
                  : key === 'fuel_type'
                    ? t('TRIPLOG.RECEIPTS.TABLE.ELECTRIC')
                    : key === 'charging_type'
                      ? hoveredReceipt.charging_type === 'public'
                        ? t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PUBLIC')
                        : hoveredReceipt.charging_type === 'home'
                          ? t('TRIPLOG.RECEIPTS.TABLE.CHARGING_HOME')
                          : hoveredReceipt.charging_type === 'package'
                            ? t('TRIPLOG.RECEIPTS.TABLE.CHARGING_PACKAGE')
                            : '-'
                      : key === 'units'
                        ? formatReceiptUnits(hoveredReceipt.units)
                        : hoveredReceipt[key] ?? '-'}
      </span>
    </div>
  )
})}
    {hoveredReceipt?.bonus_paid === true && (
            <div style={{ marginBottom: 4, display: 'flex' }}>
              <strong style={{ minWidth: 200 }}>
                {t('TRIPLOG.RECEIPTS.TABLE.BONUS_AMOUNT', { defaultValue: 'Výška bonusu' })}:
              </strong>
              <span style={{ color: '#1e3f44', fontWeight: 600 }}>
                {formatBonusAmount(hoveredReceipt)}
              </span>
            </div>
          )}
        </div>
      )}

{showFullscreenHint && (
  <div
    style={{
      position: 'fixed',
      top: cursorPos.y - 50,
      left: cursorPos.x,
      transform: 'translateX(-50%)',
      background: 'rgba(0,0,0,0.85)',
      color: '#E7DFEC',
      padding: '6px 14px 8px 14px',
      borderRadius: 12,
      fontSize: 16,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: 5000,
      border: '1.5px solid #1e3f44',
      boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      lineHeight: 1
    }}
  >
    {typeof showFullscreenHint === 'string'
  ? showFullscreenHint
  : t('MODAL_DETAILS')}
  </div>
)}

<ReceiptListModal
  open={receiptListFullscreen}
  openExportDropdown={openExportDropdown}
  onClose={() => {
    setReceiptListFullscreen(false)
    setOpenExportDropdown(false)
  }}
  receipts={receipts}
  filterCriteria={filterCriteria}
  setFilterCriteria={setFilterCriteria}
/>

      <ReceiptDetail
        open={receiptDetailOpen}
        onClose={() => {
          setReceiptDetailOpen(false)
          setSelectedReceipt(null)
          setActiveRow(null)
        }}
        receipt={selectedReceipt}
        onSaved={handleReceiptSaved}  
        onPrevReceipt={handlePrevReceipt}
        onNextReceipt={handleNextReceipt}
        hasPrevReceipt={selectedReceiptIndex > 0}
        hasNextReceipt={selectedReceiptIndex >= 0 && selectedReceiptIndex < sortedReceipts.length - 1}
      />

<ReceiptDeleteModal
  open={deleteModalOpen}
  receipt={deleteReceipt}
  onClose={() => {
    setDeleteModalOpen(false)
    setDeleteReceipt(null)
  }}
  onDeleted={() => {
    setDeleteModalOpen(false)
    setDeleteReceipt(null)
    refreshTripsAndReceipts({ silent: true })
  }}
  t={t}
/>
{tripDetailOpen && (
<TripDetail
  open={tripDetailOpen}
  onClose={() => {
    setTripDetailOpen(false)
    setSelectedTrip(null)
  }}
  trip={selectedTrip}
  onSaved={handleReceiptSaved}
/>
)}
<ReceiptAddModal
  open={addReceiptOpen}
  userId={user?.id}
  onClose={() => setAddReceiptOpen(false)}
  onSaved={(opts) => {
    setAddReceiptOpen(false)
    handleReceiptSaved(opts)
  }}
/>
    </div>
  )
}

export default ReceiptsBox